import { Link, useLocation } from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import type { NavIconProps } from '@/navigation';

export function NavMain({
  items,
  sectionLabel,
}: {
  items: {
    title: string;
    url: string;
    icon?: ComponentType<NavIconProps>;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
  sectionLabel?: string;
}) {
  const { t } = useTranslation('navigation');
  const location = useLocation();
  const { open } = useSidebar();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <SidebarGroup>
      {sectionLabel && <SidebarGroupLabel>{t(sectionLabel)}</SidebarGroupLabel>}
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) =>
            item.items ? (
              <Collapsible
                asChild
                className='group/collapsible'
                key={item.title}
                open={item.isActive}
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <Link
                      onMouseEnter={() => setHoveredItem(item.title)}
                      onMouseLeave={() => setHoveredItem(null)}
                      to={item.url}
                    >
                      <SidebarMenuButton
                        className={(() => {
                          const urlBase = item.url?.split('?').shift();
                          const pathIncludesUrl = location.pathname.includes(
                            `${urlBase}`,
                          );
                          const pathDepth = location.pathname.split('/').length;

                          if (
                            pathIncludesUrl &&
                            (pathDepth === 2 || (pathDepth === 3 && !open))
                          ) {
                            return 'rounded-md bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent';
                          }
                          return 'hover:bg-sidebar-accent/60';
                        })()}
                        tooltip={t(item.title)}
                      >
                        {item.icon && (
                          <item.icon
                            className='size-4'
                            isAnimating={hoveredItem === item.title}
                          />
                        )}
                        <span>{t(item.title)}</span>
                      </SidebarMenuButton>
                    </Link>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items?.map((subItem) => (
                        <SidebarMenuSubItem key={t(subItem.title)}>
                          <SidebarMenuSubButton
                            asChild
                            className={
                              location.pathname.includes(
                                `${subItem.url?.split('?').shift()}`,
                              )
                                ? 'rounded-md bg-sidebar-accent text-sidebar-accent-foreground'
                                : 'hover:bg-sidebar-accent/60'
                            }
                          >
                            <Link to={subItem.url}>
                              <span>{t(subItem.title)}</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            ) : (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  isActive={item.isActive}
                  tooltip={t(item.title)}
                >
                  <Link
                    className='flex items-center gap-1 pl-2 hover:bg-sidebar-accent/60'
                    onMouseEnter={() => setHoveredItem(item.title)}
                    onMouseLeave={() => setHoveredItem(null)}
                    to={item.url}
                  >
                    {item.icon && (
                      <item.icon
                        className='size-4'
                        isAnimating={hoveredItem === item.title}
                      />
                    )}
                    <span>{t(item.title)}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ),
          )}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
